import React, {useEffect, useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import styled from 'styled-components'
import PasswordInput from '../../../components/PasswordInput'
import {devices} from '../../../device'
import ServerError from '../../../errors/server'
import {isNullOrBlankString} from '../../../responseRefiners'
import {ResetPassword} from '../../../store/reset-password/reset-password.types'
import {heading} from '../../../styled/mixins'
import Alert from '../Alert'
import Button from '../Button'
import FormField from '../FormField'
import * as Typography from '../Typography'
import {isValid, ResetPasswordErrors, validate} from './validation'

interface Props {
  error?: ServerError | null
  submitting: boolean
  onSubmit(payload: ResetPassword): void
  onClearError: () => void
  token: string | null
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const Heading = styled(Typography.BoldBody1)`
  ${heading}
`

type ResetPasswordFormValues = {
  password: string
  repeatedPassword: string
}

const AlertWrapper = styled.div`
  text-align: center;
  margin-top: 1rem;

  @media ${devices.tablet} {
    margin-top: 0;
  }
`

function ResetPasswordForm({
  error = null,
  submitting,
  onSubmit,
  token,
  onClearError
}: Props) {
  const intl = useIntl()
  const [values, setValues] = useState<ResetPasswordFormValues>({
    password: '',
    repeatedPassword: ''
  })
  const [errors, setErrors] = useState<ResetPasswordErrors>({
    password: '',
    repeatedPassword: ''
  })
  const [serverError, setServerError] = useState<string | null>(null)
  const alertRef = useRef<HTMLDivElement>(null)

  function setPartialError(errorKey: keyof ResetPasswordErrors, error: string) {
    setErrors(errs => ({...errs, [errorKey]: error}))
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const errors = validate(values.password, values.repeatedPassword, intl)
    setErrors(errors)
    if (isValid(errors) && token) {
      onSubmit({
        password: values.password,
        token
      })
    } else {
      onClearError()
    }
  }

  useEffect(() => {
    if (error != null) {
      setServerError('registration.resetPassword_ServerError_generic')
    } else {
      setServerError(null)
    }
  }, [error])

  useEffect(() => {
    if (serverError != null && alertRef.current != null) {
      alertRef.current.scrollIntoView()
    }
  }, [serverError, alertRef])

  return (
    <Form onSubmit={e => handleSubmit(e)} noValidate>
      {serverError && (
        <AlertWrapper>
          <Alert type="error" ref={alertRef}>
            <FormattedMessage id={serverError} />
          </Alert>
        </AlertWrapper>
      )}
      <Heading as="h1">
        <FormattedMessage id="registration.resetPassword_header1" />
      </Heading>
      <FormField>
        <PasswordInput
          errorMessage={
            isNullOrBlankString(errors.password)
              ? null
              : intl.formatMessage({id: errors.password})
          }
          id="password"
          label={intl.formatMessage({id: 'general.password'})}
          name="password"
          placeholder={intl.formatMessage({
            id: 'registration.resetPassword_newPasswordPH'
          })}
          required
          value={values.password}
          onBlur={() => setPartialError('password', '')}
          onChange={event =>
            setValues(vals => ({...vals, password: event.target.value}))
          }
        />
      </FormField>
      <FormField>
        <PasswordInput
          errorMessage={
            isNullOrBlankString(errors.repeatedPassword)
              ? null
              : intl.formatMessage({id: errors.repeatedPassword})
          }
          id="repeatedPassword"
          label={intl.formatMessage({
            id: 'registration.resetPassword_confirmPassword'
          })}
          name="repeatedPassword"
          placeholder={intl.formatMessage({
            id: 'registration.resetPassword_confirmPasswordPH'
          })}
          required
          value={values.repeatedPassword}
          onBlur={() => setPartialError('repeatedPassword', '')}
          onChange={event =>
            setValues(values => ({
              ...values,
              repeatedPassword: event.target.value
            }))
          }
        />
      </FormField>
      <Actions>
        <Button type="submit" disabled={submitting} colour="yellow">
          <FormattedMessage id="registration.resetPassword_changeButton" />
        </Button>
      </Actions>
    </Form>
  )
}

export default ResetPasswordForm
